import {useTranslations} from 'use-intl'
import {useMemo} from 'react'
import clsx from 'clsx'
import {ProductWithIds} from '~/client/spreadshop/missing-types'
import ImageComposition from '~/components/ImageComposition'
import useShopConfig from '~/hooks/useShopConfig'
import {Image, ImageFormat} from '~/gql/graphql'

type ProductTypeImageProps = {
    className?: string
    product: ProductWithIds
    appearanceId?: string
}

const ProductTypeImage = ({
    className,
    product,
    appearanceId: preferredAppearanceId,
}: ProductTypeImageProps) => {
    const t = useTranslations('productDetails.productTypeDialog')
    const {imageServerEndpoint} = useShopConfig()

    const image = useMemo<Image | undefined>(() => {
        const appearanceId = preferredAppearanceId ?? product.defaultAppearanceId
        const productTypeId = product.type?.id
        const viewId = product.defaultViewId

        if (!appearanceId || !productTypeId) {
            return undefined
        }

        const imageUrl = new URL(
            `${imageServerEndpoint}/productTypes/${productTypeId}/views/${viewId ?? '1'}/appearances/${appearanceId}.webp`
        )

        return {
            src: imageUrl.toString(),
            srcset: [100, 500, 1000]
                .map((size) => {
                    imageUrl.searchParams.set('width', String(size))
                    imageUrl.searchParams.set('height', String(size))
                    return `${imageUrl.toString()} ${size}w`
                })
                .join(','),
            altText: t('productImageAlt', {product: product.type?.name ?? 'product'}),
            format: ImageFormat.Png,
        }
    }, [
        imageServerEndpoint,
        preferredAppearanceId,
        product.type?.id,
        product.type?.name,
        product.defaultAppearanceId,
        product.defaultViewId,
        t,
    ])

    if (!image) {
        return null
    }

    return (
        <ImageComposition
            className={clsx(className)}
            image={image}
            sizes="(min-width: 640px) 1000px, 500px"
        />
    )
}

export default ProductTypeImage
